import { useEffect, useState } from 'react'
import { childrenApi } from '@/lib/children-api'
import { updatesApi, ApiError } from '@/lib/updates-api'
import { AddUpdateForm } from '@/components/updates'
import { TimelineView } from '@/components/timeline'
import { SkeletonTimeline } from '@/components/common'
import { useDeferredLoading } from '@/hooks/useDeferredLoading'
import { UPDATE_TYPES, UPDATE_TYPE_LABELS, type Update, type UpdateType } from '@/types/update'
import type { Child } from '@/types/child'

export function UpdatesPage() {
  const [children, setChildren] = useState<Child[]>([])
  const [updates, setUpdates] = useState<Update[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const showLoading = useDeferredLoading(isLoading)
  const [error, setError] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const [nextCursor, setNextCursor] = useState<string | null>(null)

  const [showAddUpdate, setShowAddUpdate] = useState(false)
  const [editingUpdate, setEditingUpdate] = useState<Update | null>(null)
  const [filterType, setFilterType] = useState<UpdateType | 'all'>('all')
  const [filterChildId, setFilterChildId] = useState<string | 'all'>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<Update[] | null>(null)
  const [isSearching, setIsSearching] = useState(false)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setIsLoading(true)
      const [childrenRes, updatesRes] = await Promise.all([
        childrenApi.list(),
        updatesApi.list(),
      ])
      setChildren(childrenRes.children)
      setUpdates(updatesRes.updates)
      setHasMore(updatesRes.hasMore)
      setNextCursor(updatesRes.nextCursor)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load data')
    } finally {
      setIsLoading(false)
    }
  }

  const loadMore = async () => {
    if (!nextCursor || isLoadingMore) return

    try {
      setIsLoadingMore(true)
      const response = await updatesApi.list({ before: nextCursor })
      setUpdates((prev) => [...prev, ...response.updates])
      setHasMore(response.hasMore)
      setNextCursor(response.nextCursor)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load more updates')
    } finally {
      setIsLoadingMore(false)
    }
  }

  const handleUpdateSuccess = (update: Update) => {
    if (editingUpdate) {
      // Replace the edited update in the list
      setUpdates((prev) => prev.map((u) => (u.id === update.id ? update : u)))
      setEditingUpdate(null)
    } else {
      // Add new update to the beginning
      setUpdates((prev) => [update, ...prev])
      setShowAddUpdate(false)
    }
  }

  const handleEditUpdate = (update: Update) => {
    setEditingUpdate(update)
    setShowAddUpdate(false)
  }

  const handleDeleteUpdate = async (updateId: string) => {
    if (!confirm('Delete this update?')) return

    try {
      await updatesApi.delete(updateId)
      setUpdates((prev) => prev.filter((u) => u.id !== updateId))
      if (searchResults) {
        setSearchResults((prev) => prev?.filter((u) => u.id !== updateId) ?? null)
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to delete update')
    }
  }

  const handleSearch = async (query: string) => {
    setSearchQuery(query)

    if (!query.trim()) {
      setSearchResults(null)
      return
    }

    setIsSearching(true)
    try {
      const response = await updatesApi.search(query.trim())
      setSearchResults(response.updates)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Search failed')
    } finally {
      setIsSearching(false)
    }
  }

  const clearSearch = () => {
    setSearchQuery('')
    setSearchResults(null)
  }

  // Use search results if searching, otherwise filter regular updates
  const baseUpdates = searchResults !== null ? searchResults : updates
  const filteredUpdates = baseUpdates.filter((u) => {
    const matchesType = filterType === 'all' || u.type === filterType
    const matchesChild = filterChildId === 'all' || u.children.some((c) => c.id === filterChildId)
    return matchesType && matchesChild
  })

  if (showLoading) {
    return (
      <div className="p-4">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold">Updates</h1>
        </div>
        <SkeletonTimeline count={5} />
      </div>
    )
  }

  // Still loading but not showing skeleton yet - render nothing to prevent flash
  if (isLoading) {
    return null
  }

  return (
    <div className="p-4">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-bold">Updates</h1>
        {!showAddUpdate && !editingUpdate && children.length > 0 && (
          <button onClick={() => setShowAddUpdate(true)} className="btn-primary">
            Add Update
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 rounded bg-red-100 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Search Bar */}
      <div className="mb-4">
        <div className="relative">
          <input
            type="text"
            placeholder="Search updates..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-10 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
          <svg
            className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          {isSearching && (
            <svg className="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 animate-spin text-gray-400" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          )}
          {searchQuery && !isSearching && (
            <button
              onClick={clearSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        {searchResults !== null && (
          <p className="mt-1 text-sm text-gray-500">
            {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for "{searchQuery}"
          </p>
        )}
      </div>

      {showAddUpdate && (
        <div className="mb-6 rounded border border-gray-200 p-4">
          <h2 className="mb-3 text-lg font-medium">New Update</h2>
          <AddUpdateForm
            children={children}
            onSuccess={handleUpdateSuccess}
            onCancel={() => setShowAddUpdate(false)}
            filterCheckedInOnly={true}
          />
        </div>
      )}

      {editingUpdate && (
        <div className="mb-6 rounded border border-primary-200 bg-primary-50 p-4">
          <h2 className="mb-3 text-lg font-medium">Edit Update</h2>
          <AddUpdateForm
            children={children}
            editUpdate={editingUpdate}
            onSuccess={handleUpdateSuccess}
            onCancel={() => setEditingUpdate(null)}
          />
        </div>
      )}

      {children.length === 0 ? (
        <div className="rounded border border-dashed border-gray-300 p-8 text-center text-gray-500">
          <p>No children added yet.</p>
          <p className="mt-1 text-sm">Add children first to start logging updates.</p>
        </div>
      ) : (
        <>
          {/* Filters */}
          <div className="mb-4 space-y-3">
            {/* Type Filter */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setFilterType('all')}
                className={`rounded-full px-3 py-1 text-sm ${
                  filterType === 'all'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All Types
              </button>
              {UPDATE_TYPES.map((t) => (
                <button
                  key={t}
                  onClick={() => setFilterType(t)}
                  className={`rounded-full px-3 py-1 text-sm ${
                    filterType === t
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {UPDATE_TYPE_LABELS[t]}
                </button>
              ))}
            </div>

            {/* Child Filter */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setFilterChildId('all')}
                className={`rounded-full px-3 py-1 text-sm ${
                  filterChildId === 'all'
                    ? 'bg-primary-600 text-white'
                    : 'bg-primary-50 text-primary-700 hover:bg-primary-100'
                }`}
              >
                All Children
              </button>
              {children.map((child) => (
                <button
                  key={child.id}
                  onClick={() => setFilterChildId(child.id)}
                  className={`rounded-full px-3 py-1 text-sm ph-no-capture ${
                    filterChildId === child.id
                      ? 'bg-primary-600 text-white'
                      : 'bg-primary-50 text-primary-700 hover:bg-primary-100'
                  }`}
                >
                  {child.firstName}
                </button>
              ))}
            </div>
          </div>

          {/* Timeline */}
          <section>
            <h2 className="mb-4 text-lg font-medium">Timeline</h2>
            {filteredUpdates.length === 0 ? (
              <div className="rounded-lg border border-dashed border-gray-300 p-6 text-center text-gray-500">
                <p className="font-medium">
                  {filterType === 'all' && filterChildId === 'all'
                    ? 'No updates yet'
                    : 'No matching updates'}
                </p>
                <p className="mt-1 text-sm">
                  {filterType === 'all' && filterChildId === 'all'
                    ? 'Add your first update using the button above'
                    : 'Try selecting different filters'}
                </p>
              </div>
            ) : (
              <TimelineView
                updates={filteredUpdates}
                showChildren={true}
                onEdit={handleEditUpdate}
                onDelete={handleDeleteUpdate}
                hasMore={hasMore}
                isLoadingMore={isLoadingMore}
                onLoadMore={loadMore}
              />
            )}
          </section>
        </>
      )}
    </div>
  )
}
